import React, { useState, useEffect, useRef } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../api';
import KpiCard from '../components/KpiCard';

export default function Server() {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [hours, setHours] = useState(24);
  const [liveData, setLiveData] = useState([]);
  const [isLive, setIsLive] = useState(true);
  const intervalRef = useRef(null);

  // Live polling
  useEffect(() => {
    if (!isLive) { clearInterval(intervalRef.current); return; }
    const fetchLive = () => {
      api.server.live().then(d => {
        setLatest(d);
        setLiveData(prev => {
          const next = [...prev, { ...d, time: new Date(d.timestamp).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) }];
          return next.slice(-60); // utolsó 60 mérés (10 perc)
        });
      }).catch(() => {});
    };
    fetchLive();
    intervalRef.current = setInterval(fetchLive, 10000);
    return () => clearInterval(intervalRef.current);
  }, [isLive]);

  // History
  useEffect(() => {
    if (!isLive) api.server.history(hours).then(setHistory).catch(() => {});
  }, [hours, isLive]);

  const chartData = isLive ? liveData : history.map(h => ({ ...h, time: h.timestamp?.slice(11, 16) }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Szerver monitoring</h1>
          {isLive && <span style={{ background: '#34d399', width: 8, height: 8, borderRadius: '50%', display: 'inline-block', animation: 'pulse 2s infinite' }} />}
          {isLive && <span style={{ color: '#34d399', fontSize: 12, fontWeight: 600 }}>LIVE</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setIsLive(true)}
            style={{ background: isLive ? 'rgba(52,211,153,0.2)' : 'transparent', border: '1px solid #2a2e3a', color: isLive ? '#34d399' : '#8b8fa3', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: isLive ? 700 : 400 }}>
            ● LIVE
          </button>
          {[6, 12, 24, 48, 72].map(h => (
            <button key={h} onClick={() => { setIsLive(false); setHours(h); }}
              style={{ background: !isLive && h === hours ? 'rgba(0,212,255,0.2)' : 'transparent', border: '1px solid #2a2e3a', color: !isLive && h === hours ? '#00d4ff' : '#8b8fa3', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
              {h}h
            </button>
          ))}
        </div>
      </div>

      {latest && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
          <KpiCard label="CPU" value={latest.cpu_percent?.toFixed(1)} suffix="%" color={latest.cpu_percent > 80 ? '#f87171' : '#34d399'} />
          <KpiCard label="RAM" value={latest.ram_used_mb?.toFixed(0)} suffix={`/ ${latest.ram_total_mb?.toFixed(0)} MB`} color={latest.ram_used_mb / latest.ram_total_mb > 0.85 ? '#f87171' : '#34d399'} />
          <KpiCard label="Disk" value={latest.disk_used_gb?.toFixed(1)} suffix={`/ ${latest.disk_total_gb?.toFixed(0)} GB`} />
          <KpiCard label="Overpass CPU" value={latest.overpass_cpu?.toFixed(1)} suffix="%" />
          <KpiCard label="Overpass RAM" value={latest.overpass_ram_mb?.toFixed(0)} suffix="MB" />
        </div>
      )}

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>CPU {isLive ? '(real-time)' : ''}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
              <XAxis dataKey="time" tick={{ fill: '#8b8fa3', fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fill: '#8b8fa3', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#e4e6eb' }} />
              <Area type="monotone" dataKey="cpu_percent" stroke="#00d4ff" fill="rgba(0,212,255,0.15)" name="CPU %" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>RAM {isLive ? '(real-time)' : ''}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
              <XAxis dataKey="time" tick={{ fill: '#8b8fa3', fontSize: 10 }} />
              <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#e4e6eb' }} />
              <Area type="monotone" dataKey="ram_used_mb" stroke="#34d399" fill="rgba(52,211,153,0.15)" name="RAM MB" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>Overpass CPU {isLive ? '(real-time)' : ''}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
              <XAxis dataKey="time" tick={{ fill: '#8b8fa3', fontSize: 10 }} />
              <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#e4e6eb' }} />
              <Area type="monotone" dataKey="overpass_cpu" stroke="#fbbf24" fill="rgba(251,191,36,0.15)" name="Overpass CPU %" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>Overpass RAM {isLive ? '(real-time)' : ''}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
              <XAxis dataKey="time" tick={{ fill: '#8b8fa3', fontSize: 10 }} />
              <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#e4e6eb' }} />
              <Area type="monotone" dataKey="overpass_ram_mb" stroke="#fb923c" fill="rgba(251,146,60,0.15)" name="Overpass RAM MB" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
